import {useEffect, useState} from 'react';
import {useCollapsible, Action} from '@sail/ui';
import {view, css, CollapsibleContainer, Icon} from '@sail/ui/deprecated';
import {useAnalytics} from '@stripe-internal/inline-data';
import {defineMessages, useIntl} from 'react-intl';
import {
  Conversation,
  ConversationArticle,
} from '@stripe-internal/conversations-client';
import {ArticleSourcesDropdownLink} from './ArticleSourcesDropdownLink';
import {useTheme} from '../../../ThemeProvider';

const messages = defineMessages({
  viewSources: {
    id: 'conversations_ui.conversation.log.sourcesDropdown.viewSources',
    description:
      'Label for a button which expands a collapsible dropdown consisting of articles which were used as sources for providing the given answer',
    defaultMessage:
      '{sourcesCount, plural, one {View # source} other {View # sources}}',
  },
  hideSources: {
    id: 'conversations_ui.conversation.log.sourcesDropdown.hideSources',
    description:
      'Label for a button which hides a collapsible dropdown consisting of articles which were used as sources for providing the given answer',
    defaultMessage:
      '{sourcesCount, plural, one {Hide # source} other {Hide # sources}}',
  },
});

export const ArticleSourcesDropdown = ({
  conversation,
  articles,
}: {
  conversation: Conversation;
  articles: ConversationArticle[];
}) => {
  const analytics = useAnalytics();
  const theme = useTheme();
  const intl = useIntl();

  const [isOpen, setOpen] = useState(false);
  const [markedOpen, setMarkedOpen] = useState(false);
  const {triggerProps, collapsibleProps, icon} = useCollapsible({
    open: isOpen,
    iconOrientation: 'right-down',
  });

  useEffect(() => {
    if (isOpen) {
      analytics.action('opened_article_sources_dropdown');
      conversation.action({name: 'opened-article-sources-dropdown'});
      setMarkedOpen(true);
    } else if (markedOpen) {
      analytics.action('closed_article_sources_dropdown');
      conversation.action({name: 'closed-article-sources-dropdown'});
      setMarkedOpen(false);
    }
  }, [isOpen, analytics, markedOpen, conversation]);

  const rows = articles.map((article) => (
    <ArticleSourcesDropdownLink
      article={article}
      conversation={conversation}
      key={`article-source-${article.id}`}
    />
  ));

  if (articles.length === 0) {
    return <view.div data-testid="article-sources-dropdown" />;
  }

  return (
    <view.div
      data-testid="article-sources-dropdown"
      css={{
        stack: 'y',
        gap: 'xsmall',
        width: css.value('100%'),
      }}
    >
      <Action
        onPress={() => {
          setOpen(!isOpen);
        }}
        {...triggerProps}
        css={{
          stack: 'x',
          alignX: 'start',
          alignY: 'center',
          gap: 'small',
          fontWeight: 'label.small.emphasized',
          color: theme.linkColor,
        }}
      >
        <Icon
          icon={icon}
          size="xsmall"
          css={{
            color: css.value(theme.linkColor),
            height: css.value('8px'),
            width: css.value('8px'),
          }}
        />
        {intl.formatMessage(
          isOpen ? messages.hideSources : messages.viewSources,
          {sourcesCount: articles.length},
        )}
      </Action>
      <CollapsibleContainer {...collapsibleProps}>
        <view.div
          css={{
            stack: 'y',
            gap: 'medium',
            paddingY: 'medium',
          }}
        >
          {rows}
        </view.div>
      </CollapsibleContainer>
    </view.div>
  );
};
